import { RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import {
  HttpError,
  type IHttpClient,
} from '@webapp/infrastructure/interfaces/services/IHttpClient'
import { type IPersistentStorage } from '@webapp/infrastructure/interfaces/services/IPersistentStorage'
import { type ChangelogReleaseConstructor } from '@webapp/platform/discover-more/entities/ChangelogRelease'
import { type ChangelogReleaseApiModel } from '../../api-models/ChangelogReleaseApiModel'
import { mapChangelogReleaseApiModel } from '../../mappers/mapChangelogReleaseApiModel'

interface Dependency {
  httpClient: IHttpClient
  persistentStorage: IPersistentStorage
}

const CHANGELOG_PATH = '/changelog'

/**
 * `visibility` taxonomy term marking a release as featured (slug `featured`).
 * Used only to filter server-side; the mapper reads the embedded term slug so
 * the ID is not load-bearing for detection.
 */
const FEATURED_VISIBILITY_TERM_ID = 694

/**
 * Window of newest releases fetched in one request. Wide enough to still yield
 * five displayable releases after the ones without a hero image are dropped.
 */
const RELEASES_WINDOW_SIZE = 20

export class ChangelogRepository {
  static readonly RELEASES_STORAGE_KEY =
    'procurify.discover-more.changelog-releases'

  constructor(private readonly deps: Dependency) {}

  /**
   * Newest releases, with the newest featured release guaranteed to be in the
   * set even when it falls outside the newest-`RELEASES_WINDOW_SIZE` window
   * (fetched with a second, server-side-filtered request).
   *
   * Successful responses are persisted, and a failed fetch falls back to that
   * cache so the promo surfaces show the last known releases instead of an
   * empty state. With no cache to fall back on, the error propagates and the
   * caller decides what to render.
   */
  async getLatestReleases(): Promise<ChangelogReleaseConstructor[]> {
    try {
      const [latestResponse, featuredResponse] = await Promise.all([
        // The newest window carries the releases, so its failure propagates to
        // the cache fallback below. The featured request only adds a pin —
        // losing it costs the pin, not the whole load.
        this.fetchReleases({ per_page: RELEASES_WINDOW_SIZE }),
        this.fetchReleases({
          per_page: 1,
          visibility: FEATURED_VISIBILITY_TERM_ID,
        }).catch(() => []),
      ])

      const releases = this.dedupeById([
        ...featuredResponse,
        ...latestResponse,
      ]).map(mapChangelogReleaseApiModel)

      if (releases.length > 0) {
        this.deps.persistentStorage.set(
          ChangelogRepository.RELEASES_STORAGE_KEY,
          releases
        )
      }

      return releases
    } catch (err) {
      const cached = this.deps.persistentStorage.get<
        ChangelogReleaseConstructor[]
      >(ChangelogRepository.RELEASES_STORAGE_KEY)

      if (cached?.length) {
        return cached
      }

      if (err instanceof HttpError) {
        throw new RepoError({
          code: null,
          message: err.message,
          status: err.status,
          response: err.response,
          headers: err.headers,
        })
      }

      throw err
    }
  }

  private async fetchReleases(
    params: Record<string, number>
  ): Promise<ChangelogReleaseApiModel[]> {
    const res = await this.deps.httpClient.get<ChangelogReleaseApiModel[]>(
      CHANGELOG_PATH,
      { params: { orderby: 'date', order: 'desc', _embed: 1, ...params } }
    )

    return res.data ?? []
  }

  private dedupeById(
    releases: ChangelogReleaseApiModel[]
  ): ChangelogReleaseApiModel[] {
    const seen = new Set<number>()

    return releases.filter((release) => {
      if (seen.has(release.id)) {
        return false
      }

      seen.add(release.id)

      return true
    })
  }
}
